import { useRef, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { Trash2, UploadCloud } from 'lucide-react'

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { DateTimePickerInput } from '@/shared/ui/datetime-picker-input'
import { Label } from '@/shared/ui/label'
import { Textarea } from '@/shared/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import apiClient from '@/shared/lib/apiClient'
import { t } from '@/shared/lib/i18n'
import { showSuccessToast } from '@/shared/lib/toast'
import { instrumentServiceReportsPath } from '@/shared/lib/routes'
import { nowInTimeZone, toWallClockString } from '@/shared/lib/tenantDateTime'
import type { SharedProps } from '@/shared/types/SharedProps'

const DESCRIPTION_MAX_LENGTH = 5000

type AllowedFileTypes = { mimeTypes: string[]; extensions: string[] }

const buildIsAllowedFile = (allowedFileTypes: AllowedFileTypes) => {
  const mimeSet = new Set(allowedFileTypes.mimeTypes)
  const extSet = new Set(allowedFileTypes.extensions)

  return (file: File): boolean => {
    if (file.type && mimeSet.has(file.type)) return true
    const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
    return extSet.has(extension)
  }
}

type LoggerOption = { id: number; label: string }

type AddServiceReportDialogProps = {
  instrumentId: number
  loggerOptions: LoggerOption[]
  siteTimeZone: string
  allowedFileTypes: AllowedFileTypes
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const AddServiceReportDialog = ({
  instrumentId,
  loggerOptions,
  siteTimeZone,
  allowedFileTypes,
  open,
  onOpenChange,
}: AddServiceReportDialogProps) => {
  const { currentUserInfo } = usePage<SharedProps>().props
  const isAllowedFile = buildIsAllowedFile(allowedFileTypes)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [completedAt, setCompletedAt] = useState(() => toWallClockString(new Date(), siteTimeZone))
  const [description, setDescription] = useState('')
  const [loggerId, setLoggerId] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const resetForm = () => {
    setCompletedAt(toWallClockString(new Date(), siteTimeZone))
    setDescription('')
    setLoggerId('')
    setFiles([])
    setErrorMessage(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) resetForm()
    onOpenChange(nextOpen)
  }

  const addSelectedFiles = (fileList: FileList | null) => {
    if (!fileList) return
    const selectedFiles = Array.from(fileList)
    const rejected = selectedFiles.filter((file) => !isAllowedFile(file))
    if (rejected.length > 0) {
      setErrorMessage(t('instruments.show.serviceReports.invalidFileType'))
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    setErrorMessage(null)
    setFiles((current) => [...current, ...selectedFiles])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removeFile = (index: number) => {
    setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!description.trim()) {
      setErrorMessage(t('instruments.show.serviceReports.descriptionRequired'))
      return
    }

    setSubmitting(true)
    setErrorMessage(null)

    const formData = new FormData()
    formData.append('problem_description', description.trim())
    // E36-S3: submit the raw wall-clock string; the backend resolves the instrument's site
    // zone and converts to UTC via SiteTimeZone.wall_clock_to_utc.
    if (completedAt) formData.append('completed_at', completedAt)
    if (loggerId) formData.append('lab_logger_id', loggerId)
    files.forEach((file) => formData.append('files[]', file))

    apiClient
      .post(instrumentServiceReportsPath(instrumentId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(() => {
        resetForm()
        onOpenChange(false)
        showSuccessToast(t('instruments.show.serviceReports.saved'))
        router.reload({ only: ['reportsCalendar', 'instrument', 'serviceReports'] })
      })
      .catch((error) => {
        const message = error?.response?.data?.error
        setErrorMessage(
          typeof message === 'string' ? message : t('instruments.show.serviceReports.saveFailed'),
        )
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('instruments.show.serviceReports.dialogTitle')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errorMessage && (
            <div
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              role="alert"
            >
              {errorMessage}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="service-report-completed-at">
              {t('instruments.show.serviceReports.completedAtLabel')}
            </Label>
            <DateTimePickerInput
              id="service-report-completed-at"
              value={completedAt}
              onChange={setCompletedAt}
              timeZone={siteTimeZone}
              maxDate={nowInTimeZone(siteTimeZone)}
              showNowButton={false}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="service-report-description">
              {t('instruments.show.serviceReports.descriptionLabel')}{' '}
              <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="service-report-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={4}
              maxLength={DESCRIPTION_MAX_LENGTH}
              placeholder={t('instruments.show.serviceReports.descriptionPlaceholder')}
              className="field-sizing-fixed resize-y"
            />
            <p className="text-right text-xs text-muted-foreground">
              {description.length} / {DESCRIPTION_MAX_LENGTH}
            </p>
          </div>

          {loggerOptions.length > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="service-report-logger">
                {t('instruments.show.serviceReports.loggerLabel')}
              </Label>
              <Select value={loggerId || undefined} onValueChange={setLoggerId}>
                <SelectTrigger id="service-report-logger">
                  <SelectValue
                    placeholder={t('instruments.show.serviceReports.loggerPlaceholder')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {loggerOptions.map((option) => (
                    <SelectItem key={option.id} value={String(option.id)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="service-report-files">
              {t('instruments.show.serviceReports.attachmentsLabel')}
            </Label>
            <label
              htmlFor="service-report-files"
              className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 px-4 py-5 text-center transition-colors hover:bg-muted/50"
            >
              <UploadCloud className="h-6 w-6 text-muted-foreground" />
              <span className="text-sm font-medium text-primary">
                {t('instruments.show.serviceReports.chooseFiles')}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('instruments.show.serviceReports.allowedTypes')}
              </span>
              <Input
                id="service-report-files"
                type="file"
                multiple
                ref={fileInputRef}
                className="sr-only"
                onChange={(event) => addSelectedFiles(event.target.files)}
              />
            </label>
            {files.length > 0 && (
              <ul className="space-y-1">
                {files.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-sm"
                  >
                    <span className="truncate">{file.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={() => removeFile(index)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center gap-1 text-sm">
            <span className="text-muted-foreground">
              {t('instruments.show.serviceReports.completedByLabel')}:
            </span>
            <span className="font-medium">{currentUserInfo?.displayName ?? '—'}</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)}>
              {t('instruments.show.serviceReports.cancel')}
            </Button>
            <Button type="submit" disabled={submitting || !description.trim()}>
              {submitting
                ? t('instruments.show.serviceReports.saving')
                : t('instruments.show.serviceReports.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddServiceReportDialog
